import { dehydrate, type QueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/nextjs';

export interface ServerQuery {
  queryKey: unknown[];
  queryFn: Function;
}

/**
 *  Fetches required react-query requests on the server
 *  #### Example request:
 *  queryOnServer([{
 *    queryKey: ["site-footer"],
 *    queryFn: getFooterTemplate
 *  }])
 *
 * @param queries
 * @param queryClient
 * @returns `Promise<DehydratedState>`
 */
export async function queryOnServer(queries: ServerQuery[], queryClient: QueryClient) {
  const results = await Promise.allSettled(
    queries.map(async ({ queryKey, queryFn }) => {
      // @ts-expect-error: queryFn is a function, as typed in `ServerQuery`
      return queryClient.prefetchQuery(queryKey, queryFn, { staleTime: 60000 });
    }),
  );

  results.forEach((result, index) => {
    if (result.status === 'rejected') {
      const queryKey = queries[index].queryKey;
      console.error(`Error prefetching query: ${queryKey}`, result.reason);
      Sentry.addBreadcrumb({
        category: 'ssr_prefetch',
        level: 'warning',
        message: 'react-query prefetch rejected',
        data: {
          query_key: JSON.stringify(queryKey),
          reason: result.reason instanceof Error ? result.reason.message : String(result.reason),
        },
      });
    }
  });

  return dehydrate(queryClient);
}
